/**
 * AdditionalTableTab — F12S Etap E (inline cell editing) + atomic-save Krok 6 (buffered mode).
 *
 * Renders an additional table's rows under a parent record.
 * Fetches field definitions and records via React Query.
 *
 * UX (WF-Desktop style):
 *  - Clicking a data cell activates inline editing of that cell only.
 *  - Enter / blur → PATCH (only if value changed). Escape → cancel, no network.
 *  - Bottom add-row: hidden until „Dodaj wiersz” (button under the table, Document items
 *    pattern) is clicked; stays open for the next entry, closes on ✕ / Escape. A draft
 *    with typed values keeps it visible across remounts.
 *  - 'reference' fields open a record picker (`AdditionalReferenceCell`), both in an
 *    existing row and in the bottom add-row. The picker is unavailable in exactly two
 *    cases: the field has no configured target (legacy column, `relationSystemModuleSlug`
 *    is null), or the configured target has no entry in `lib/system-entity-adapters.ts`
 *    — each renders an explanatory message instead of a trigger.
 *  - Delete (trash icon): buffered mode marks the row immediately — it stays visible,
 *    struck through, with „Przywróć” until the page is saved (no confirm: nothing is lost
 *    yet). HTTP mode deletes on the server at once, so it goes through
 *    <ConfirmDialog variant="danger">; zero window.confirm.
 *  - Buffered mode marks unsaved rows (new / changed / removed — `RowStatus`), offers
 *    „Cofnij zmiany” on a changed row and shows a pending-changes counter + legend.
 *  - `editable` prop (default `true`) — `false` renders the whole tab read-only:
 *    no click-to-edit, no reference picker triggers, no add-row, no delete.
 *    Backward-compatible default so unwired callers/tests keep today's
 *    always-interactive behavior; every page-level caller is expected to pass
 *    its own `editing` state.
 *
 * Wymaga `TenantAuthProvider` w drzewie (nowe od celu odwołania w tabelach dodatkowych):
 * komórka `reference` czyta zakres aktywnej firmy przez `useActiveCompanyScope()`, a ten
 * sięga po `useTenantAuth()`. Wcześniej komponent nie zależał od żadnego kontekstu auth,
 * więc testy/strony montujące go „gołego" muszą teraz dostarczyć provider albo mock.
 *
 * Buffered mode (`buffered` prop, default `false` — Krok 6 atomic-save refactor,
 * controlled-buffer fix — Krok 7 data-loss bugfix):
 *  - When `false` (default): byte-for-byte unchanged — auto-saves every row
 *    insert/update/delete over HTTP, exactly as before. All consumers except
 *    PurchaseInvoiceDetail stay on this default.
 *  - When `true`: row mutations are NOT sent over HTTP. Instead the delta
 *    buffer (`bufferedValue` prop, shape mirrors the backend `additionalTables`
 *    body field — see `apps/api/app/api/purchase-invoices/route.ts`) is a
 *    CONTROLLED prop OWNED BY THE PARENT — this component holds NO local
 *    buffer state. Every mutation (add/edit/remove row) computes the new
 *    slice from the CURRENT `bufferedValue` prop + the change and calls
 *    `onDeltasChange(tableId, newSlice)` synchronously in the event handler
 *    (no effect, no local buffer state, no report-on-mount). The parent
 *    aggregates slices from every `AdditionalTableTab` instance (one per
 *    additional table) and sends them in the single save request.
 *
 *    Why controlled: the shared `Tabs` component unmounts inactive panels.
 *    Previously the buffer lived in local `useState` and was reported to the
 *    parent via a mount-effect that ALSO fired on every mount with EMPTY
 *    maps — switching tabs away and back remounted this component, the
 *    mount-effect fired with empty deltas, and the parent unconditionally
 *    overwrote its (non-empty) slice with the empty one, silently losing
 *    buffered rows before save. Making the buffer a controlled prop means a
 *    remount just re-reads the parent's still-intact value — nothing to lose.
 *
 *    New unsaved rows get a client-generated `tempId`, unique even across
 *    remounts (derived from `bufferedValue.inserted`, not a local counter —
 *    see `makeTempId`).
 *
 * Add-row draft (`addRowDraft`/`getAddRowDraft`/`onAddRowDraftChange` props — Bug 2
 * lost-on-tab-switch fix): the bottom add-row's IN-PROGRESS, not-yet-confirmed values
 * are a SECOND controlled surface, for the exact same "Tabs unmounts inactive panels"
 * reason as `bufferedValue` above — MODE-INDEPENDENT (applies whether or not
 * `buffered` is set, since the loss happens in the typing phase before confirm, not
 * after). Unlike `bufferedValue`, this one has a hybrid fallback: when the parent
 * doesn't supply `onAddRowDraftChange`, the draft lives in a local `useState` instead
 * of being lost outright — safe because an uncontrolled draft only ever loses itself
 * on an unwired remount, it never reports upward, so it can't clobber parent state
 * the way the pre-Krok-7 local buffer did. Cleared explicitly (not via a remount key —
 * see the removed `addRowResetKey`) right after a successful add, in both the HTTP and
 * buffered paths.
 *
 * Etap 10.5 (ADR-0066 §11) — błąd materializacji słownika zewnętrznego zwrócony
 * przez `useDictionary`/`useDictionaryOptions` (`error`, patrz `DictionaryQueryError`)
 * jest renderowany jako czytelny komunikat pod kontrolką select/multiselect
 * (`mapDictionaryQueryError`, `useFieldSelectOptions`, `FieldSelectControl`) —
 * dropdown zostaje pusty (bez crasha), user widzi PRZYCZYNĘ, nie tylko efekt.
 *
 * Układ kolumn (`columnSettings` = `element.settings` umieszczenia tabeli na formatce): kolejność,
 * widoczność, własna etykieta, wyrównanie, szerokość i miejsca po przecinku liczą się w
 * `resolveEmbeddedTableColumns` (`@saas/shared`); ta sama lista widocznych kolumn rysuje nagłówek,
 * wiersze i wiersz dodawania. Bez `columnSettings` (albo bez zapisanego układu) rysunek jest
 * dokładnie taki jak przed wprowadzeniem układu.
 */
import { type KeyboardEvent, memo, type RefObject, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { effectiveColumnAlign, resolveEmbeddedTableColumns } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2, Undo2, X } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldFileInput, FieldFileLink } from '@/components/fields/FieldFileInput';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { RecordTablePicker, type RecordTablePickerRow } from '@/components/ui/RecordTablePicker';
import { useToast } from '@/contexts/ToastContext';
import {
  type DictionaryQueryError,
  formatDictionaryOptionLabel,
  hasParamBindings,
  mapDictionaryQueryError,
  type ParentFieldBindingKey,
  useDictionary,
  useDictionaryOptions,
} from '@/hooks/useDictionary';
import {
  additionalFieldsKey,
  type AdditionalParentValues,
  additionalRecordsTableKey,
  collectParentFieldSlugs,
  collectParentFieldSlugsForFields,
  fetchAdditionalTableFields,
  pickParentValues,
} from '@/lib/additional-tables/parent-field-values';
import type { AdditionalField } from '@/lib/additional-tables/types';
import { api } from '@/lib/api';
import { additionalTableDescriptors } from '@/lib/embedded-table/additional-table-descriptors';
import {
  columnAlignClass,
  fixedTableMinWidth,
  formatDecimalCell,
  hasExplicitWidths,
  TableColgroup,
} from '@/lib/embedded-table/cells';
import { resolveReferenceLabel } from '@/lib/ext-list-columns';
import type { FieldFileMeta, FieldFileTarget } from '@/lib/field-files/api';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { fromUserDateTimeInput, toUserDateTimeInput } from '@/lib/user-date-time';
import { cn } from '@/lib/utils';

import { InlineEditableCell } from './InlineEditableCell';

/** Mapa etykiet odwołań (`extRefLabels`) — kontrakt jak na listach kartotek, patrz
 *  `lib/ext-fields/resolve-ext-ref-labels.ts` po stronie backendu. */
type ExtRefLabelMap = Record<string, Record<string, string | null>>;

// ─── API Types ────────────────────────────────────────────────────────────────

// `AdditionalField` (definicja pola tabeli) żyje w `lib/additional-tables/types.ts`.

interface AdditionalRecord {
  id: string;
  parent_id: string;
  position: number;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

// ─── Buffered-mode delta types (Krok 6 atomic-save refactor) ──────────────────
// Mirrors the backend `additionalTables` body field shape (one-table slice —
// the parent aggregates slices from every AdditionalTableTab instance keyed
// by tableId before sending the single save request).

export interface AdditionalTableInsertedDelta {
  tableId: string;
  tempId: string;
  position?: number;
  values: Record<string, unknown>;
}

export interface AdditionalTableUpdatedDelta {
  tableId: string;
  recordId: string;
  values: Record<string, unknown>;
}

export interface AdditionalTableRemovedDelta {
  tableId: string;
  recordId: string;
}

/** Per-table delta slice reported by a single buffered AdditionalTableTab instance. */
export interface AdditionalTableDeltaSlice {
  inserted: AdditionalTableInsertedDelta[];
  updated: AdditionalTableUpdatedDelta[];
  removed: AdditionalTableRemovedDelta[];
}

export interface AdditionalTableMeta {
  id: string;
  slug: string;
  displayName: string;
  parentTableName: string;
  parentEntity: string;
  allowAddRow: boolean;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

function additionalRecordsKey(tableId: string, parentId: string) {
  return [...additionalRecordsTableKey(tableId), parentId] as const;
}

function additionalTablesKey() {
  return ['additional-tables'] as const;
}

// ─── Układ kolumn (settings elementu layoutu) ─────────────────────────────────

/**
 * Szerokość (px) kolumny akcji (Usuń / ✓) — używana tylko, gdy tabela ma jawne szerokości
 * kolumn danych (`table-layout: fixed`); bez nich kolumna akcji dopasowuje się sama.
 */
const ACTIONS_COLUMN_WIDTH_PX = 96;

// ─── Znaczniki niezapisanych zmian (tryb buforowany) ──────────────────────────
// Pasek przy lewej krawędzi pierwszej komórki — ten sam dla wiersza dodawania i dodanego,
// jeszcze niezapisanego wiersza; zmieniony wiersz ma pasek bursztynowy, a wiersz do usunięcia
// zostaje widoczny, przekreślony, z przyciskiem „Przywróć” (zmiany zapisują się dopiero z całą
// kartoteką, więc usunięcie da się cofnąć).
const NEW_ROW_CLASS =
  'bg-accent-50/40 dark:bg-accent-900/10 [&>td:first-child]:border-l-2 [&>td:first-child]:border-l-accent-500';
const CHANGED_ROW_CLASS = '[&>td:first-child]:border-l-2 [&>td:first-child]:border-l-amber-500';
const REMOVED_ROW_CLASS = 'bg-bg-secondary/60 text-text-tertiary line-through';

/** Stan wiersza względem serwera — w trybie bez bufora zawsze `saved`. */
type RowStatus = 'saved' | 'new' | 'changed' | 'removed';

const ROW_STATUS_CLASS: Record<RowStatus, string> = {
  saved: 'hover:bg-bg-secondary/30',
  new: NEW_ROW_CLASS,
  changed: cn(CHANGED_ROW_CLASS, 'hover:bg-bg-secondary/30'),
  removed: REMOVED_ROW_CLASS,
};

/** Metadane pliku z wartości komórki kolumny „plik” (ADR-0114); `undefined` gdy brak/nieznane. */
function fileMetaForCell(
  value: unknown,
  fileMeta: Readonly<Record<string, FieldFileMeta>>,
): FieldFileMeta | undefined {
  return typeof value === 'string' && value !== '' ? fileMeta[value] : undefined;
}

/** Czy szkic wiersza dodawania ma jakąkolwiek wpisaną wartość (wtedy wiersz zostaje widoczny). */
function draftHasValues(draft: AdditionalTableAddRowDraft): boolean {
  return Object.values(draft.values).some(
    (v) => v != null && v !== '' && !(Array.isArray(v) && v.length === 0),
  );
}

/** Kolumna do narysowania: pole + ustawienia prezentacji wynikające z układu. */
interface ColumnView {
  /** Slug pola — identyfikator kolumny w układzie. */
  id: string;
  field: AdditionalField;
  /** Etykieta nagłówka: własna z układu albo nazwa pola. */
  label: string;
  /** Klasa wyrównania; brak = dotychczasowe zachowanie (nagłówek w lewo, komórki bez klasy). */
  alignClass?: string;
  /** Jawna szerokość w px (brak = auto). */
  width?: number;
  /** Miejsca po przecinku kolumny `numeric` — wyłącznie prezentacja. */
  decimals?: number;
}

/**
 * Widoczne kolumny w kolejności układu. Kolumny wymagane są zawsze widoczne (rozwiązuje to
 * `resolveEmbeddedTableColumns`), więc walidacja wymaganych pól wiersza dodawania zostaje
 * kompletna. Wyrównanie: jawne `align` z układu, domyślnie lewo (deskryptory tabeli
 * dodatkowej) — lewo nie dokłada klasy, więc bez układu render zostaje jak dotąd.
 */
function buildColumnViews(
  fields: readonly AdditionalField[],
  columnSettings: Record<string, unknown> | undefined,
): ColumnView[] {
  const fieldBySlug = new Map(fields.map((field) => [field.slug, field] as const));
  const resolved = resolveEmbeddedTableColumns(additionalTableDescriptors(fields), columnSettings);
  const views: ColumnView[] = [];
  for (const col of resolved) {
    if (!col.visible) continue;
    const field = fieldBySlug.get(col.id);
    if (!field) continue;
    const align = effectiveColumnAlign(col);
    views.push({
      id: col.id,
      field,
      label: col.label ?? field.name,
      alignClass: align === 'left' ? undefined : columnAlignClass(align),
      width: col.width,
      decimals: col.decimals,
    });
  }
  return views;
}

/**
 * Klasy wyrównania wartości w natywnym `<select>`: sam `text-align` nie wystarcza — Chromium
 * wyrównuje wybraną wartość zwiniętej listy wg `text-align-last`. Lewo = brak klasy (bez układu
 * render jak dotąd).
 */
function selectAlignClassName(alignClassName: string | undefined): string | undefined {
  if (alignClassName === columnAlignClass('center')) return 'text-center [text-align-last:center]';
  if (alignClassName === columnAlignClass('right')) return 'text-right [text-align-last:right]';
  return alignClassName;
}

/**
 * Wyrównanie `Checkbox` z biblioteki: ignoruje on `text-align` — opakowuje pole w kolumnę flex
 * z etykietą na całą szerokość, więc położenie ustawia `align-items` kontenera (przez
 * `containerClassName`). Lewo = brak klasy (stretch, pole przy lewej krawędzi).
 */
function checkboxContainerAlignClass(alignClassName: string | undefined): string | undefined {
  if (alignClassName === columnAlignClass('center')) return 'items-center';
  if (alignClassName === columnAlignClass('right')) return 'items-end';
  return undefined;
}

/**
 * Kompaktowy `Textarea` w wierszu tabeli: domyślne `px-3 py-2 leading-[22px]` zawyżałoby wiersz
 * ponad pola `Input size="sm"`. Wykrzykniki, bo `Textarea` składa klasy bez `tailwind-merge`
 * (o zwycięzcy decydowałaby kolejność w arkuszu).
 */
const COMPACT_TEXTAREA_CLASS = '!px-2 !py-1 !leading-5';

// ─── Type-aware equality comparators (M1, M2) ─────────────────────────────────

/**
 * Returns an isEqual comparator appropriate for the given field type.
 *
 * - date: compare on the 10-char YYYY-MM-DD slice (pg returns full ISO, input
 *   uses the sliced form — without this a re-selected unchanged date fires PATCH).
 * - timestamp: compare the minute shown in the control (app time zone) — the server returns
 *   the moment with `Z`, the control emits it back as ISO; same minute = no change.
 * - integer / numeric: coerce both sides to Number (pg returns numeric columns
 *   as strings; the input onChange coerces to Number — mismatched types).
 * - everything else: strict ===.
 */
function makeCellIsEqual(
  fieldType: string,
): ((committedValue: unknown, draft: unknown) => boolean) | undefined {
  switch (fieldType) {
    case 'date':
      return (a, b) => {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;
        return String(a).slice(0, 10) === String(b).slice(0, 10);
      };
    case 'timestamp':
      return (a, b) => {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;
        return toUserDateTimeInput(a) === toUserDateTimeInput(b);
      };
    case 'integer':
    case 'numeric':
      return (a, b) => {
        if (a == null && b == null) return true;
        if (a == null || b == null) return false;
        return Number(a) === Number(b);
      };
    default:
      return undefined; // plain === in the hook
  }
}

// ─── Select/multiselect option resolution (Etap 4b — dictionary-aware) ───────

/**
 * Resolves the option list for a select/multiselect additional-table field:
 * inline `field.options` by default, or — when `field.dictionaryCode` is set —
 * the dictionary's selectable items (option value = item code, label formatted
 * per the dictionary's `displayMode`). Mirrors `resolveSelectOptions` in
 * `ExtFieldValueInput` (kept local here since this file's `AdditionalField`
 * shape differs slightly and duplicating one small function is cheaper than a
 * cross-cutting shared type).
 *
 * Etap 10.3 (ADR-0066 §11) route selection: a field whose
 * `dictionaryParamBindings` is NON-EMPTY fetches through
 * `useDictionaryOptions` (`POST /dictionaries/:code/options`) with
 * `recordParams` built from the CURRENT sibling values of this row
 * (`rowValues` — the record row for existing cells, the local add-row state
 * for the bottom add-row). A field with no bindings keeps the existing
 * `useDictionary` (`GET /dictionaries/:code`) path — zero regression.
 *
 * Both dictionary hooks are called UNCONDITIONALLY every render (rules of
 * hooks) — whichever route this field does NOT use gets an empty `code` /
 * `enabled: false`, which is already a documented no-op for both hooks.
 *
 * Fetch-on-open (review fix): the param-bound route additionally gates on
 * `optionsFetchArmed` — true only while the control is open: `<select>` via
 * `FieldSelectControl`'s `onFocus`/`onBlur` (mirroring `optionsFetchArmed` in
 * `ExtFieldValueInput` / `isOpen` in `SelectFieldType`), `MultiSelect` via its
 * own `onOpenChange` callback (see `FieldSelectControl` doc — focus/blur
 * bubbling proved unreliable for the multiselect case). Without this gate
 * `AddRow` — which renders a `FieldInput` per column UNCONDITIONALLY on every
 * render, never opened — fired the POST immediately on mount with an
 * empty/partial `recordParams`. The non-param `useDictionary` route is
 * unaffected (cheap, cached GET).
 *
 * Wiązanie `parent_field`: gdy wołający poda `parentKey` (`'default'` / klasa dokumentu) i pole
 * ma wiązania wskazujące pola rodzica dla tego klucza, do body żądania dochodzą `parentValues`
 * (WYŁĄCZNIE te sluggi, tylko skalary — nieskalar/brak na ekranie → `null`) oraz `parentKey`.
 * Wartości trafiają też do klucza zapytania (`useDictionaryOptions`), więc zmiana pola rodzica
 * na ekranie daje świeże zapytanie przy następnym otwarciu kontrolki. Bez `parentKey` (wołający
 * nie podaje rodzica) nic z rodzica nie jest wysyłane — serwer zgłosi brak parametru.
 */
function useFieldSelectOptions(
  field: AdditionalField,
  rowValues: Record<string, unknown>,
  optionsFetchArmed: boolean,
  parentValues?: Record<string, unknown>,
  parentKey?: ParentFieldBindingKey,
): {
  options: Array<{ value: string; label: string }>;
  /** True while a configured dictionary hasn't resolved yet — disables the
   *  control instead of rendering it empty/interactive. */
  isLoading: boolean;
  /**
   * Etap 10.5 (ADR-0066 §11) — błąd materializacji słownika zewnętrznego z
   * WHICHEVER ścieżka jest aktywna (tak jak `dictionary`/`activeItems`
   * powyżej): param-bound pola mogą dostać EXTERNAL_PARAM_MISSING/INVALID z
   * `useDictionaryOptions`; statyczne pola (bez wiązań, ale sourceKind='external'
   * z parametrami context.*) mogą dostać EXTERNAL_SOURCE_UNAVAILABLE z
   * `useDictionary`. `null` gdy pole nie ma `dictionaryCode` (fallback na
   * `field.options`, zero fetcha, zero błędu).
   */
  error: DictionaryQueryError | null;
} {
  const dictionaryCode = field.dictionaryCode ?? '';
  const fieldHasParamBindings = hasParamBindings(field.dictionaryParamBindings);

  const parentSlugs =
    fieldHasParamBindings && parentKey !== undefined
      ? collectParentFieldSlugs(field.dictionaryParamBindings, parentKey)
      : [];
  const sendParent = parentKey !== undefined && parentSlugs.length > 0;

  const staticResult = useDictionary(fieldHasParamBindings ? '' : dictionaryCode);
  // Faza 2b (ADR-0068): RAW wiązania pola + wartości wiersza idą do serwera —
  // rozwiązywanie wartości per parametr (w tym `context.*`) robi endpoint options,
  // nie klient (dawny `buildDictionaryRecordParams` usunięty).
  const dynamicResult = useDictionaryOptions(fieldHasParamBindings ? dictionaryCode : '', {
    bindings: fieldHasParamBindings ? field.dictionaryParamBindings : null,
    recordValues: rowValues,
    parentValues: sendParent ? pickParentValues(parentValues ?? {}, parentSlugs) : undefined,
    parentKey: sendParent ? parentKey : undefined,
    enabled: fieldHasParamBindings && optionsFetchArmed,
  });

  const {
    dictionary,
    activeItems,
    isLoading: dictLoading,
    error: dictError,
  } = fieldHasParamBindings ? dynamicResult : staticResult;

  if (field.dictionaryCode) {
    if (!dictionary) return { options: [], isLoading: dictLoading, error: dictError };
    return {
      options: activeItems.map((item) => ({
        value: item.code,
        label: formatDictionaryOptionLabel(
          item,
          field.dictionaryDisplayMode ?? dictionary.displayMode,
        ),
      })),
      isLoading: false,
      error: dictError,
    };
  }
  return {
    options: (field.options ?? []).map((o) => ({ value: String(o.value), label: o.label })),
    isLoading: false,
    error: null,
  };
}

/**
 * Keeps a previously-stored value visible even if its dictionary item (or
 * inline option) is no longer in the resolved list — otherwise editing an
 * existing row would silently show an empty/mismatched selection.
 */
function ensureOptionPresent(
  options: Array<{ value: string; label: string }>,
  value: string,
): Array<{ value: string; label: string }> {
  if (!value) return options;
  const lowerValue = value.toLowerCase();
  if (options.some((o) => o.value.toLowerCase() === lowerValue)) return options;
  return [...options, { value, label: value }];
}

// ─── FieldSelectControl — select/multiselect widget (Etap 4b) ────────────────

/**
 * Renders the `select` or `multiselect` widget for a field. Extracted into
 * its own component so `useFieldSelectOptions` is called UNCONDITIONALLY at
 * this component's top level — `FieldInput`'s switch renders this component
 * only for the 'select'/'multiselect' branches, which is fine (conditionally
 * rendering a *component* is not the same as conditionally calling a *hook*;
 * see react-hooks/rules-of-hooks).
 *
 * Fetch-on-open gate (review fix, Etap 10.3 follow-up): the param-bound
 * dictionary fetch (`useDictionaryOptions`) is armed ONLY while this control
 * is open. Native `<select>` has no open/close event, so focus/blur is the
 * accepted approximation (same one `SelectFieldType`/`ExtFieldValueInput`
 * use). `MultiSelect` DOES track its own open state internally, so it is
 * armed/disarmed through its `onOpenChange` callback instead — an EARLIER
 * version of this fix wrapped `MultiSelect` in a `<div onFocus/onBlur>`
 * catching bubbled focus/blur, but that was unreliable: closing the dropdown
 * unmounts its internal `<input autoFocus>` search box, and a blur/focusout
 * fired on a node as it's being removed from the DOM does not reliably
 * bubble to ancestors — the wrapper stayed armed FOREVER after the first
 * open (verified by a regression test, `AdditionalTableTab.test.tsx` TC-22).
 * `AddRow` renders this component for every column UNCONDITIONALLY on every
 * render but never opens it, so without a gate a param-bound column fired
 * the POST immediately on tab render with an empty/partial `recordParams`.
 *
 * Render guard (review fix, Finding B): the gate is armed only when the
 * field ALSO has a truthy `dictionaryCode` — bindings without a target
 * dictionary have nothing to fetch, so no focus/open tracking is attached at
 * all for that (misconfigured) field, see `shouldArmOptionsFetch` below.
 */
function FieldSelectControl({
  field,
  value,
  onChange,
  onKeyDown,
  onBlur,
  autoFocus,
  testIdPrefix,
  rowValues,
  parentValues,
  parentKey,
  alignClassName,
}: {
  field: AdditionalField;
  value: unknown;
  onChange: (v: unknown) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  testIdPrefix: string;
  /** Current values of the OTHER columns of this same row — see `FieldInput`. */
  rowValues: Record<string, unknown>;
  /** Wartości pól rekordu-rodzica z ekranu + klucz wyboru pola — see `FieldInput`. */
  parentValues?: Record<string, unknown>;
  parentKey?: ParentFieldBindingKey;
  /**
   * Klasa wyrównania z układu kolumn — dostaje ją tylko natywny `<Select>` (jego `className`
   * trafia na element `<select>`). `MultiSelect` pomija ją świadomie: jego `className` ląduje na
   * kontenerze zewnętrznym, a przycisk-wyzwalacz ma wyrównanie wpisane na stałe (`text-left`,
   * wybrane wartości to elementy flex), więc klasa nic by nie zmieniła; zmiana wymagałaby nowego
   * API prymitywu biblioteki UI.
   */
  alignClassName?: string;
}) {
  const { t } = useTranslation();
  const [optionsFetchArmed, setOptionsFetchArmed] = useState(false);
  const armOptionsFetch = () => setOptionsFetchArmed(true);
  const disarmOptionsFetch = () => setOptionsFetchArmed(false);

  const {
    options,
    isLoading,
    error: dictionaryError,
  } = useFieldSelectOptions(field, rowValues, optionsFetchArmed, parentValues, parentKey);
  // Etap 10.5 — czytelny komunikat zamiast pustego dropdownu bez wyjaśnienia.
  const dictionaryErrorMessage = mapDictionaryQueryError(dictionaryError, t);

  const fieldHasParamBindings = hasParamBindings(field.dictionaryParamBindings);
  // Finding B: nothing to fetch without a target dictionary — don't attach
  // any fetch-arm machinery (onFocus/onBlur, onOpenChange) for that case.
  const shouldArmOptionsFetch = fieldHasParamBindings && !!field.dictionaryCode;

  if (field.fieldType === 'multiselect') {
    const current = Array.isArray(value) ? (value as string[]) : [];
    const optionsWithStale = current.reduce((acc, code) => ensureOptionPresent(acc, code), options);
    // Etap 10.5 — `<MultiSelect>` has no `error` prop (unlike `<Select>`
    // below) — no error: return the exact same single element as before
    // this stage; with an error, append a `<p role="alert">` (Fragment).
    const multiSelectEl = (
      <MultiSelect
        options={optionsWithStale}
        value={current}
        onChange={(v) => onChange(v)}
        disabled={isLoading}
        data-testid={`${testIdPrefix}-${field.slug}`}
        // Fetch-on-open for param-bound fields with a resolvable dictionary
        // (see file-header comment above) — `undefined` for every other
        // field, so `MultiSelect`'s own rendering/behavior is untouched.
        onOpenChange={shouldArmOptionsFetch ? setOptionsFetchArmed : undefined}
      />
    );
    if (!dictionaryErrorMessage) return multiSelectEl;
    return (
      <>
        {multiSelectEl}
        <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {dictionaryErrorMessage}
        </p>
      </>
    );
  }

  const currentValue = value != null ? String(value) : '';
  const optionsWithStale = ensureOptionPresent(options, currentValue);
  const selectOptions = field.isRequired
    ? optionsWithStale
    : [{ value: '', label: '—' }, ...optionsWithStale];
  return (
    <Select
      options={selectOptions}
      value={currentValue}
      className={selectAlignClassName(alignClassName)}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={isLoading}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLSelectElement> | undefined}
      // Etap 10.5 — `<Select>` renders this text below the control itself
      // (`role="alert"`) — `undefined` when there's no error, zero behavior
      // change.
      error={dictionaryErrorMessage ?? undefined}
      // Fetch-on-open for param-bound fields (see `optionsFetchArmed` above).
      // Non-param (or dictionaryCode-less) fields: no onFocus at all — zero
      // behavior change.
      onFocus={shouldArmOptionsFetch ? armOptionsFetch : undefined}
      onBlur={() => {
        // Disarming is a no-op (state already false) when the fetch was
        // never armed — the cell-edit commit callback below must ALWAYS
        // still fire.
        disarmOptionsFetch();
        onBlur?.();
      }}
      autoFocus={autoFocus}
      data-testid={`${testIdPrefix}-${field.slug}`}
    />
  );
}

// ─── FieldInput — per-type input widget (shared by cell-edit and add-row) ────

/**
 * Renders the correct input widget for a field type.
 * 'reference' returns null ON PURPOSE — that type has its own cell renderer
 * (`AdditionalReferenceCell`), see the `case 'reference'` comment below.
 */
function FieldInput({
  field,
  value,
  onChange,
  onKeyDown,
  onBlur,
  autoFocus,
  inputRef,
  testIdPrefix = 'additional-field',
  rowValues,
  parentValues,
  parentKey,
  alignClassName,
}: {
  field: AdditionalField;
  value: unknown;
  onChange: (v: unknown) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** Prefix for data-testid. Defaults to 'additional-field'. Use a different
   *  prefix when the same field slug appears multiple times in the DOM
   *  (e.g. 'additional-add-field' for the bottom add-row). */
  testIdPrefix?: string;
  /**
   * Klasa wyrównania tekstu pola edycji z układu kolumn — natywne `<input>`/`<textarea>`/`<select>`
   * nie dziedziczą `text-align` z komórki, więc dostają ją wprost (select dodatkowo
   * `text-align-last`; `Checkbox` — `align-items` kontenera, patrz `checkboxContainerAlignClass`).
   * Multiselect i odwołanie jej nie czytają (patrz komentarz w `FieldSelectControl`).
   */
  alignClassName?: string;
  /**
   * Current values of the OTHER columns of this same row (Etap 10.3) — the
   * `record` row for an existing-row cell edit, the local add-row `values`
   * state for the bottom add-row. Only consumed by the select/multiselect
   * branch (param-bound external dictionaries), ignored by every other type.
   */
  rowValues: Record<string, unknown>;
  /**
   * Wartości pól REKORDU-RODZICA tabeli (kartoteki właściciela) z ekranu — źródło wiązań
   * `parent_field`; `parentKey` wybiera slug pola z wiązania (`'default'` / klasa dokumentu).
   * Tylko gałąź select/multiselect je czyta (patrz `useFieldSelectOptions`).
   */
  parentValues?: Record<string, unknown>;
  parentKey?: ParentFieldBindingKey;
}) {
  // Wspólne właściwości pól tekstowych/liczbowych/dat — `Input` z biblioteki UI (rozmiar `sm` =
  // zbity wiersz tabeli); klasa wyrównania z układu kolumn dochodzi wyłącznie przy jawnym `align`.
  const inputRefTyped = inputRef as RefObject<HTMLInputElement | null> | undefined;
  const sharedProps = {
    onKeyDown: onKeyDown as React.KeyboardEventHandler<HTMLInputElement> | undefined,
    onBlur,
    autoFocus,
    'data-testid': `${testIdPrefix}-${field.slug}`,
  };

  switch (field.fieldType) {
    case 'varchar':
    case 'email':
    case 'url':
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type={field.fieldType === 'varchar' ? 'text' : field.fieldType}
          className={alignClassName}
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value)}
          {...sharedProps}
        />
      );

    case 'text':
      return (
        <Textarea
          ref={inputRef as RefObject<HTMLTextAreaElement | null> | undefined}
          rows={2}
          className={cn(COMPACT_TEXTAREA_CLASS, alignClassName)}
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLTextAreaElement> | undefined}
          onBlur={onBlur}
          autoFocus={autoFocus}
          data-testid={`${testIdPrefix}-${field.slug}`}
        />
      );

    case 'integer':
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type="number"
          step={1}
          className={alignClassName}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number.parseInt(v, 10));
          }}
          {...sharedProps}
        />
      );

    case 'numeric':
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type="number"
          step="any"
          className={alignClassName}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number.parseFloat(v));
          }}
          {...sharedProps}
        />
      );

    case 'date':
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type="date"
          className={alignClassName}
          value={value ? String(value).slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          {...sharedProps}
        />
      );

    case 'timestamp':
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type="datetime-local"
          className={alignClassName}
          value={toUserDateTimeInput(value)}
          onChange={(e) => onChange(fromUserDateTimeInput(e.target.value))}
          {...sharedProps}
        />
      );

    case 'boolean':
      return (
        <Checkbox
          ref={inputRefTyped}
          checked={Boolean(value)}
          containerClassName={checkboxContainerAlignClass(alignClassName)}
          onChange={(e) => onChange(e.target.checked)}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLInputElement> | undefined}
          onBlur={onBlur}
          autoFocus={autoFocus}
          data-testid={`${testIdPrefix}-${field.slug}`}
        />
      );

    case 'select':
    case 'multiselect':
      // Etap 4b: prawdziwy dropdown — opcje inline (`field.options`) albo ze
      // słownika (`field.dictionaryCode`); logic + the `useFieldSelectOptions`
      // hook call live in `FieldSelectControl` (must be called unconditionally
      // from a component that's ALWAYS rendered on this branch, not inline
      // here where it would be a conditional hook call).
      return (
        <FieldSelectControl
          field={field}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          autoFocus={autoFocus}
          testIdPrefix={testIdPrefix}
          rowValues={rowValues}
          parentValues={parentValues}
          parentKey={parentKey}
          alignClassName={alignClassName}
        />
      );

    case 'reference':
      // Obsługiwane przez własny renderer komórki (`AdditionalReferenceCell` w
      // `InlineCellTd`/`AddRow`), nie przez ten generyczny switch inputów — modal
      // pickera otwarty wewnątrz draftu `InlineEditableCell` kolidowałby z jej
      // mechanizmem `onBlur`-commit. Usunięcie tego case'a spadłoby na `default` i
      // pokazałoby goły input tekstowy zamiast pickera — gorzej niż jawny `null`.
      return null;

    default:
      return (
        <Input
          ref={inputRefTyped}
          size="sm"
          type="text"
          className={alignClassName}
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value)}
          {...sharedProps}
        />
      );
  }
}

// ─── Cell display helper ──────────────────────────────────────────────────────

/**
 * Tekst komórki. `decimals` (z układu kolumn, tylko kolumny `numeric`) → liczba z zadaną
 * liczbą miejsc po przecinku; bez niego, albo dla wartości pustej/nieliczbowej, format jak
 * dotąd. Tylko wyświetlanie — wartość w polu edycji i w bazie zostaje surowa.
 */
function displayCellValue(value: unknown, decimals?: number): string {
  const formatted = formatDecimalCell(value, decimals);
  if (formatted !== null) return formatted;
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return String(value);
}

/**
 * Etykieta rozwiązana z `extRefLabels[field.slug]`, TYLKO gdy klucz faktycznie tam jest
 * (`Object.hasOwn` — brak klucza = nierozwiązywalne, `resolveReferenceLabel` samo w sobie
 * zwróciłoby wtedy surowe id jako fallback, czego tu NIE chcemy: wołający ma dostać
 * `undefined`, żeby `AdditionalReferenceCell` mogło spaść na `lastPicked` lokalny, zanim
 * sięgnie po skrócone id — patrz precedencja w tym komponencie). Reużywa jedną regułę
 * kompozycji etykiety (string / sentinel „skasowany") z `ext-list-columns.tsx` — bez
 * drugiej kopii.
 */
function resolvedReferenceLabelOrUndefined(
  idStr: string | null,
  refLabelsForField: Record<string, string | null> | undefined,
  t: (key: string) => string,
): string | undefined {
  if (!idStr || !refLabelsForField || !Object.hasOwn(refLabelsForField, idStr)) return undefined;
  return resolveReferenceLabel(idStr, refLabelsForField, t);
}

/**
 * Etykieta odwołania dla JEDNEJ komórki — wołana w pętli wiersza (gdzie `t` jest już
 * dostępne z głównego komponentu), NIE wewnątrz `InlineCellTd`. Dzięki temu `useTranslation`
 * nie jest wołane raz na KAŻDĄ komórkę tabeli (także tę, która odwołaniem nie jest), a
 * `InlineCellTd` dostaje gotowy string — prymityw, więc `memo` nadal działa.
 * `undefined` dla pól innych niż `reference` oraz gdy backend nie rozwiązał tej wartości.
 */
function referenceLabelForCell(
  field: AdditionalField,
  record: AdditionalRecord,
  extRefLabels: ExtRefLabelMap,
  t: (key: string) => string,
): string | undefined {
  if (field.fieldType !== 'reference') return undefined;
  const raw = record[field.slug];
  const idStr = typeof raw === 'string' && raw !== '' ? raw : null;
  return resolvedReferenceLabelOrUndefined(idStr, extRefLabels[field.slug], t);
}

// ─── AdditionalReferenceCell — picker dla pola `reference` (mirror ReferenceRecordInput) ──

interface AdditionalReferenceCellProps {
  field: AdditionalField;
  value: unknown;
  /** Etykieta już rozwiązana przez backend (`extRefLabels`) — `undefined` = brak wpisu
   *  (nierozwiązywalne LUB wartość jeszcze nieodświeżona po commit). */
  resolvedLabel: string | undefined;
  /**
   * Etykieta z mapy rodzica (`pickedLabels` w `AdditionalTableTab`), kluczowanej
   * `${recordId}:${fieldSlug}` — przetrwała ewentualne przemontowanie TEJ komórki
   * (np. przejście z `AddRow` do właściwego wiersza po dodaniu), w odróżnieniu od
   * `lastPicked` niżej, który jest lokalny wyłącznie dla bieżącej instancji.
   */
  optimisticLabel?: string;
  onChange: (id: string | null, label?: string | null) => void;
  testId: string;
}

/**
 * Picker dla pola typu `reference` w tabeli dodatkowej — mirror `ReferenceRecordInput`
 * (`ExtFieldValueInput.tsx`), z dwiema świadomymi różnicami:
 *
 *  1. Etykieta priorytetyzuje `extRefLabels` (już rozwiązany przez backend, ten sam
 *     kontrakt co listy kartotek) zamiast osobnego zapytania o szczegół rekordu —
 *     GET records i tak już to niesie (patrz `resolveAdditionalRefLabels` po stronie
 *     backendu). Precedencja: `resolvedLabel` (backend) → `lastPicked` (lokalny stan,
 *     natychmiastowy feedback zanim refetch po commit dotrze) → skrócone id.
 *  2. `companyId` JEST przekazywane do pickera, przez `useActiveCompanyScope()` —
 *     odstępstwo od `ReferenceRecordInput`, które świadomie tego nie robi (tam firma
 *     rekordu jest nieznana komponentowi). Tutaj interceptor axiosa i tak wysyła (w
 *     typowym przypadku) dokładnie tę wartość jako `X-Company-Id` na tym samym żądaniu
 *     GET records, które backend zużywa zarówno do `canAccessAdditionalParent`, jak i do
 *     `resolveAdditionalRefLabels` — spójny zakres dla pickera i dla etykiety. Hook i
 *     interceptor NIE są matematycznie identyczne (hook liczy pełny fallback-chain,
 *     interceptor czyta localStorage wprost) — rozbieżność (rzadka: puste localStorage)
 *     idzie zawsze w bezpieczną stronę: picker węższy niż zakres, który i tak zaakceptuje
 *     backend, nigdy odwrotnie. Zawężenie stosujemy tylko dla kartotek, które adapter
 *     oznacza jako firmowo-zakresowe (`scopeByActiveCompany`) — dla celu „firmy" jest
 *     wyłączone, bo `?companyId=` zwraca tam JEDNĄ firmę i wybór innej byłby niemożliwy.
 */
function AdditionalReferenceCell({
  field,
  value,
  resolvedLabel,
  optimisticLabel,
  onChange,
  testId,
}: AdditionalReferenceCellProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Etykieta ostatnio wybranego rekordu W TEJ SAMEJ instancji komórki (bez remountu) —
   * natychmiastowy feedback, zanim refetch po commit przyniesie `extRefLabels` z
   * backendu. Dla przypadku, gdy KOMÓRKA SIĘ ZMIENIA (np. `AddRow` → właściwy wiersz po
   * dodaniu), patrz `optimisticLabel` z propsów (mapa `pickedLabels` w rodzicu) — TO
   * przetrwa taką zmianę, `lastPicked` poniżej nie.
   *
   * ZNANE OGRANICZENIE (świadomie poza zakresem): nawet `optimisticLabel` w rodzicu żyje
   * tylko w stanie `AdditionalTableTab` — nie przeżywa przełączenia zakładek `Tabs`
   * (cała `AdditionalTableTab` jest wtedy odmontowywana, nie tylko `AddRow`). Po powrocie
   * do zakładki niezapisany wiersz pokaże skrócone UUID zamiast nazwy, aż do zapisu.
   * Pełna naprawa wymagałaby podniesienia etykiety aż do bufora `bufferedValue`
   * kontrolowanego przez stronę-rodzica (ten sam poziom co reszta delty).
   */
  const [lastPicked, setLastPicked] = useState<{ id: string; label: string } | null>(null);
  const companyScope = useActiveCompanyScope();

  const targetSlug = field.relationSystemModuleSlug ?? null;
  const adapter = getSystemEntityAdapter(targetSlug);
  const recordId = typeof value === 'string' && value !== '' ? value : null;

  // Kartoteka wskazana, ale nieobsługiwana w runtime (slug spoza mapy adapterów).
  if (targetSlug && !adapter) {
    return (
      <span data-testid={testId} className="text-xs italic text-text-muted">
        {t('common.extField.referencePicker.unsupported', { slug: targetSlug })}
      </span>
    );
  }

  // Pole nie ma skonfigurowanego celu (legacy kolumna sprzed decyzji #1/#3). Własny klucz
  // i18n (NIE wspólny `common.extField.referencePicker.notConfigured` z pól kartoteki) —
  // ścieżka konfiguracji celu dla tabel dodatkowych jest inna (Ustawienia → Tabele
  // dodatkowe), więc reużyty komunikat kierowałby na zły ekran.
  if (!adapter) {
    return (
      <span data-testid={testId} className="text-xs italic text-text-muted">
        {t('additionalTables.referenceTarget.notConfigured')}
      </span>
    );
  }

  const label = recordId
    ? (resolvedLabel ??
      optimisticLabel ??
      (lastPicked?.id === recordId ? lastPicked.label : null) ??
      `${recordId.slice(0, 8)}…`)
    : null;

  // Zakres firmowy pickera — `companyIdParam` (lib/use-active-company.ts) jest jednym
  // źródłem reguły „tryb «Wszystkie firmy» = brak parametru"; adapter może to zawężanie
  // wyłączyć (`scopeByActiveCompany: false` — dziś cel „firmy").
  const pickerCompanyId =
    adapter.scopeByActiveCompany === false ? undefined : companyIdParam(companyScope);

  return (
    <div data-testid={testId} className="flex items-center gap-1.5">
      <span
        className="min-w-0 flex-1 truncate text-sm text-text-primary"
        data-testid={`${testId}-label`}
        title={label ?? undefined}
      >
        {label ?? t('common.extField.referencePicker.empty')}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setPickerOpen(true)}
        aria-label={t(
          recordId
            ? 'common.extField.referencePicker.changeFor'
            : 'common.extField.referencePicker.selectFor',
          { field: field.name },
        )}
        data-testid={`${testId}-trigger`}
      >
        {recordId
          ? t('common.extField.referencePicker.change')
          : t('common.extField.referencePicker.select')}
      </Button>
      {recordId && (
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => {
            setLastPicked(null);
            onChange(null, null);
          }}
          aria-label={t('common.extField.referencePicker.clearFor', { field: field.name })}
          data-testid={`${testId}-clear`}
        >
          <X aria-hidden="true" />
        </IconButton>
      )}
      {/* Montowany dopiero po otwarciu — inaczej KAŻDY wiersz trzymałby własną instancję
          pickera (z jej obserwatorami zapytań) mimo zamkniętego modala. */}
      {pickerOpen && (
        <RecordTablePicker
          entity={adapter.slug}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          value={recordId}
          companyId={pickerCompanyId}
          onChange={(pickedId, row: RecordTablePickerRow) => {
            const pickedLabel = adapter.getLabel(row);
            setLastPicked({ id: pickedId, label: pickedLabel });
            onChange(pickedId, pickedLabel);
          }}
          data-testid={`${testId}-picker`}
        />
      )}
    </div>
  );
}

// ─── InlineCellTd — one editable table cell ───────────────────────────────────

interface InlineCellTdProps {
  field: AdditionalField;
  record: AdditionalRecord;
  /** `false` renders a plain, non-interactive cell — no click-to-edit, no reference
   *  picker trigger. Required (not optional): this is a file-internal component with
   *  a single call site, so a default here would just hide a wiring mistake. */
  editable: boolean;
  onCommitCell: (recordId: string, fieldSlug: string, newValue: unknown) => void;
  /** Gotowa etykieta odwołania (`referenceLabelForCell`, liczona w wierszu) — tylko dla
   *  pól `reference`, `undefined` inaczej lub gdy backend nie rozwiązał tej wartości. */
  resolvedReferenceLabel?: string;
  /** Etykieta z mapy `pickedLabels` rodzica dla TEGO (record, field) — patrz
   *  `optimisticLabel` w `AdditionalReferenceCellProps`. */
  optimisticReferenceLabel?: string;
  /** Wywoływane po wyborze/wyczyszczeniu w komórce `reference` — rodzic aktualizuje
   *  `pickedLabels`, żeby etykieta przetrwała ewentualną zmianę instancji komórki. */
  onReferenceLabelPicked?: (label: string | null) => void;
  /** Wartości pól rekordu-rodzica z ekranu + klucz — patrz `FieldInput`. */
  parentValues?: Record<string, unknown>;
  parentKey?: ParentFieldBindingKey;
  /** Klasa wyrównania z układu kolumn (komórka, wyświetlanie i pole edycji); brak = bez zmian. */
  alignClass?: string;
  /** Miejsca po przecinku z układu kolumn (tylko wyświetlanie kolumny `numeric`). */
  decimals?: number;
  /** Kolumny „plik” (ADR-0114): metadane pliku z wartości komórki (nazwa zamiast id). */
  fileMeta?: FieldFileMeta;
  /** Kolumny „plik”: gdzie wysyłać plik (tabela + rekord-rodzic). */
  fileTarget?: FieldFileTarget;
  /** Kolumny „plik”: rodzic zapamiętuje metadane świeżo wysłanego pliku. */
  onFileMeta?: (fileId: string, meta: FieldFileMeta) => void;
}

const InlineCellTd = memo(function InlineCellTd({
  field,
  record,
  editable,
  onCommitCell,
  resolvedReferenceLabel,
  optimisticReferenceLabel,
  onReferenceLabelPicked,
  parentValues,
  parentKey,
  alignClass,
  decimals,
  fileMeta,
  fileTarget,
  onFileMeta,
}: InlineCellTdProps) {
  const cellValue = record[field.slug];
  const isEqual = makeCellIsEqual(field.fieldType);

  // Kolumna „plik” (ADR-0114): własna komórka, jak `reference` — wybór pliku od razu zmienia
  // wartość (bez trybu edycji komórki), w podglądzie nazwa z pobraniem.
  if (field.fieldType === 'file') {
    const fileId = typeof cellValue === 'string' && cellValue !== '' ? cellValue : null;
    return (
      <td
        className={cn('px-2 py-1', alignClass)}
        data-testid={`additional-${editable ? 'table' : 'readonly'}-cell-${field.slug}-${record.id}`}
      >
        {editable && fileTarget ? (
          <FieldFileInput
            value={fileId}
            meta={fileMeta}
            target={fileTarget}
            onChange={(id, meta) => {
              if (id && meta) onFileMeta?.(id, meta);
              onCommitCell(record.id, field.slug, id);
            }}
            data-testid={`additional-file-${field.slug}-${record.id}`}
          />
        ) : fileId ? (
          <FieldFileLink fileId={fileId} meta={fileMeta} />
        ) : (
          <span className="px-1 text-text-secondary">—</span>
        )}
      </td>
    );
  }

  if (!editable) {
    const readOnlyDisplayValue =
      field.fieldType === 'reference'
        ? (resolvedReferenceLabel ?? optimisticReferenceLabel ?? displayCellValue(cellValue))
        : displayCellValue(cellValue, decimals);
    return (
      <td
        className={cn('px-3 py-2 text-text-secondary', alignClass)}
        data-testid={`additional-readonly-cell-${field.slug}-${record.id}`}
      >
        {readOnlyDisplayValue}
      </td>
    );
  }

  if (field.fieldType === 'reference') {
    return (
      <td className={cn('px-3 py-2', alignClass)}>
        <AdditionalReferenceCell
          field={field}
          value={cellValue}
          resolvedLabel={resolvedReferenceLabel}
          optimisticLabel={optimisticReferenceLabel}
          onChange={(id, label) => {
            onCommitCell(record.id, field.slug, id);
            onReferenceLabelPicked?.(label ?? null);
          }}
          testId={`additional-table-cell-${field.slug}-${record.id}`}
        />
      </td>
    );
  }

  return (
    <td className={cn('px-0 py-0', alignClass)}>
      <InlineEditableCell
        value={cellValue}
        onCommit={(newValue) => onCommitCell(record.id, field.slug, newValue)}
        isEqual={isEqual}
        data-testid={`additional-table-cell-${field.slug}-${record.id}`}
        className={cn('block px-3 py-2 w-full', alignClass)}
        renderDisplay={(v) => displayCellValue(v, decimals)}
        renderInput={(draft, setDraft, onKeyDown, onBlur, ref) => {
          const el = (
            <FieldInput
              field={field}
              value={draft}
              onChange={setDraft}
              onKeyDown={onKeyDown as (e: KeyboardEvent<HTMLElement>) => void}
              onBlur={onBlur}
              inputRef={ref}
              alignClassName={alignClass}
              autoFocus
              // Sibling columns of THIS row (Etap 10.3 param-bound dictionaries)
              // come from the record itself — only one cell is edited at a
              // time, so the other columns' committed values live here.
              rowValues={record}
              parentValues={parentValues}
              parentKey={parentKey}
            />
          );
          return el;
        }}
      />
    </td>
  );
});

// ─── Bottom add-row ───────────────────────────────────────────────────────────

/**
 * In-progress, not-yet-confirmed draft of the bottom add-row — `values` per field
 * slug plus `labels` for any `reference` field picks (mirrors `AddRowProps.onAdd`'s
 * second arg). Lifted to a CONTROLLED prop on `AdditionalTableTab` for the same
 * reason `bufferedValue` is (see file header, "Krok 7" bugfix) — the shared `Tabs`
 * component unmounts inactive panels, which used to silently discard whatever the
 * user had typed into this row. Mode-independent: applies whether or not `buffered`
 * is set, since the loss happens in the typing phase BEFORE confirm, not after.
 */
export interface AdditionalTableAddRowDraft {
  values: Record<string, unknown>;
  labels: Record<string, string>;
}

/** Empty draft — stable-shape default, mirrors `EMPTY_ADDITIONAL_TABLE_DELTA_SLICE`. */
export const EMPTY_ADDITIONAL_TABLE_ADD_ROW_DRAFT: AdditionalTableAddRowDraft = {
  values: {},
  labels: {},
};

interface AddRowProps {
  /** Widoczne kolumny w kolejności układu — te same, które rysuje nagłówek tabeli. */
  columns: readonly ColumnView[];
  /** Controlled draft — see `AdditionalTableAddRowDraft`. Owned by `AdditionalTableTab`
   *  (itself either controlled by the parent page via `onAddRowDraftChange`, or a
   *  local fallback `useState` when the caller doesn't wire it). */
  draft: AdditionalTableAddRowDraft;
  onDraftChange: (next: AdditionalTableAddRowDraft) => void;
  /**
   * `labels` niesie etykiety wybrane w polach `reference` tego wiersza (kluczowane
   * slugiem pola) — rodzic zapisuje je do `pickedLabels` pod tempId, który dopiero
   * przydzieli, żeby świeżo wstawiony wiersz nie pokazał skróconego UUID (patrz
   * `optimisticLabel` w `AdditionalReferenceCellProps`).
   */
  onAdd: (values: Record<string, unknown>, labels?: Record<string, string>) => void;
  adding: boolean;
  /** Zamknięcie wiersza dodawania (✕ albo Esc) — rodzic czyści szkic i chowa wiersz. */
  onCancel: () => void;
  /** Ustaw kursor w pierwszym polu — wiersz otwarty przyciskiem „Dodaj wiersz”. */
  autoFocusFirst: boolean;
  /** Wartości pól rekordu-rodzica z ekranu + klucz — patrz `FieldInput`. */
  parentValues?: Record<string, unknown>;
  parentKey?: ParentFieldBindingKey;
  /** Kolumny „plik” (ADR-0114): metadane znanych plików (nazwa zamiast id). */
  fileMeta: Record<string, FieldFileMeta>;
  /** Kolumny „plik”: cel wysyłania dla danej kolumny. */
  fileTargetFor: (fieldSlug: string) => FieldFileTarget;
  /** Kolumny „plik”: rodzic zapamiętuje metadane świeżo wysłanego pliku. */
  onFileMeta: (fileId: string, meta: FieldFileMeta) => void;
  /**
   * Zmiana jednej wartości liczona od NAJŚWIEŻSZEGO szkicu rodzica — dla zmian kończonych
   * asynchronicznie (wysyłka pliku), żeby nie nadpisać wartości wpisanych w międzyczasie.
   */
  onDraftFieldChange: (slug: string, value: unknown) => void;
}

function AddRow({
  columns,
  draft,
  onDraftChange,
  onAdd,
  adding,
  onCancel,
  autoFocusFirst,
  parentValues,
  parentKey,
  fileMeta,
  fileTargetFor,
  onFileMeta,
  onDraftFieldChange,
}: AddRowProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { values, labels } = draft;

  const setValue = (slug: string, v: unknown) =>
    onDraftChange({ ...draft, values: { ...values, [slug]: v } });
  const setReferenceValue = (slug: string, id: string | null, label: string | null | undefined) => {
    const nextValues = { ...values, [slug]: id };
    let nextLabels = labels;
    if (!label) {
      if (slug in labels) {
        nextLabels = { ...labels };
        delete nextLabels[slug];
      }
    } else {
      nextLabels = { ...labels, [slug]: label };
    }
    onDraftChange({ values: nextValues, labels: nextLabels });
  };

  function handleConfirm() {
    // m5: gate against duplicate POST while a request is in flight
    if (adding) return;

    // m6: required-field validation with visible feedback via toast
    const missing = columns.filter(
      ({ field: f }) => f.isRequired && (values[f.slug] == null || values[f.slug] === ''),
    );
    if (missing.length > 0) {
      toast.show({ variant: 'error', title: t('additionalTables.requiredFieldsMissing') });
      return;
    }

    // m4: reset values only on success (moved to onSuccess in the mutation);
    // just dispatch here without clearing yet.
    onAdd(values, labels);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }

  const firstInputSlug = columns.find(
    ({ field }) => field.fieldType !== 'reference' && field.fieldType !== 'file',
  )?.field.slug;

  return (
    <tr
      className={cn('border-b border-border-primary last:border-0', NEW_ROW_CLASS)}
      data-testid="additional-table-new-row"
    >
      {columns.map(({ field, alignClass }) => {
        if (field.fieldType === 'file') {
          const raw = values[field.slug];
          const fileId = typeof raw === 'string' && raw !== '' ? raw : null;
          return (
            <td key={field.slug} className={cn('px-2 py-1', alignClass)}>
              <FieldFileInput
                value={fileId}
                meta={fileId ? fileMeta[fileId] : undefined}
                target={fileTargetFor(field.slug)}
                onChange={(id, meta) => {
                  if (id && meta) onFileMeta(id, meta);
                  // Wysyłka kończy się asynchronicznie — zmiana idzie do bieżącego szkicu.
                  onDraftFieldChange(field.slug, id);
                }}
                data-testid={`additional-add-field-${field.slug}`}
              />
            </td>
          );
        }
        if (field.fieldType === 'reference') {
          const rawValue = values[field.slug] ?? null;
          return (
            <td key={field.slug} className={cn('px-2 py-1', alignClass)}>
              <AdditionalReferenceCell
                field={field}
                value={rawValue}
                // Wiersz jeszcze niezapisany — brak `extRefLabels` z backendu.
                resolvedLabel={undefined}
                // Etykieta ze szkicu podniesionego do rodzica (`draft.labels`) — przetrwa
                // odmontowanie/remount `AddRow` (przełączenie zakładki), inaczej niż
                // wewnętrzny `lastPicked` w AdditionalReferenceCell.
                optimisticLabel={labels[field.slug]}
                onChange={(id, label) => setReferenceValue(field.slug, id, label)}
                testId={`additional-add-field-${field.slug}`}
              />
            </td>
          );
        }
        return (
          <td key={field.slug} className={cn('px-2 py-1', alignClass)}>
            <FieldInput
              field={field}
              value={values[field.slug] ?? null}
              onChange={(v) => setValue(field.slug, v)}
              onKeyDown={handleKeyDown}
              alignClassName={alignClass}
              testIdPrefix="additional-add-field"
              autoFocus={autoFocusFirst && field.slug === firstInputSlug}
              // No onBlur commit for add-row; user must press Enter or click the ✓ button.
              // Sibling columns of THIS not-yet-saved row (Etap 10.3 param-bound
              // dictionaries) come from the local add-row draft state.
              rowValues={values}
              parentValues={parentValues}
              parentKey={parentKey}
            />
          </td>
        );
      })}
      <td className="px-2 py-1 text-right whitespace-nowrap">
        <IconButton
          size="sm"
          className="text-accent-600 hover:text-accent-700"
          onClick={handleConfirm}
          disabled={adding}
          aria-label={t('common.add')}
          title={t('common.add')}
          data-testid="additional-add-row-confirm-btn"
        >
          <Check />
        </IconButton>
        <IconButton
          size="sm"
          onClick={onCancel}
          aria-label={t('additionalTables.cancelAddRow')}
          title={t('additionalTables.cancelAddRow')}
          data-testid="additional-add-row-cancel-btn"
        >
          <X />
        </IconButton>
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/** Empty delta slice — stable-shape default for the controlled `bufferedValue` prop. */
export const EMPTY_ADDITIONAL_TABLE_DELTA_SLICE: AdditionalTableDeltaSlice = {
  inserted: [],
  updated: [],
  removed: [],
};

export interface AdditionalTableTabProps {
  /** Slug tabeli dodatkowej (np. `'certyfikaty'`). Used to resolve tableId. */
  tableSlug: string;
  /**
   * UUID rekordu-rodzica. `null`/`undefined` gdy rekord jeszcze nie istnieje
   * (tryb create, buffered) — w tym wypadku komponent NIE odpytuje serwera o
   * istniejące wiersze (nie ma ich), renderuje pustą tabelę i pozwala dodawać
   * wiersze buforowane (wymaga `buffered=true`, patrz nagłówek pliku).
   */
  parentId: string | null | undefined;
  /** Whether adding new rows is allowed (falls back to table meta allowAddRow). */
  allowAddRow?: boolean;
  /**
   * `false` makes the whole tab read-only — no click-to-edit, no reference picker
   * triggers, no add-row, no delete. Default `true` (today's always-interactive
   * behavior) so unwired callers and existing tests are unaffected; every
   * page-level caller is expected to pass its own `editing` state.
   */
  editable?: boolean;
  /**
   * Opt-in buffered mode (Krok 6 atomic-save refactor). Default `false` —
   * unchanged auto-save-over-HTTP behavior. When `true`, row mutations are
   * tracked locally and reported via `onDeltasChange` instead of firing
   * POST/PATCH/DELETE immediately. See file header for the full contract.
   */
  buffered?: boolean;
  /**
   * Controlled delta-buffer value (Krok 7 data-loss bugfix), used directly
   * when the caller already knows `tableId` up front. The component holds
   * no local buffer state, so on every render (including right after a
   * remount) it displays exactly this value overlaid on the server rows.
   * Ignored when `buffered` is false. Mutually exclusive with
   * `getBufferedValue` (that one wins if both are passed, since it is
   * needed by callers who only know `tableSlug`, not `tableId`, up front —
   * see `buildDocumentAdditionalRenderers`).
   */
  bufferedValue?: AdditionalTableDeltaSlice;
  /**
   * Getter variant of `bufferedValue` for callers that only know `tableSlug`
   * up front (this component resolves `tableId` internally from the tables
   * list query) — e.g. `buildDocumentAdditionalRenderers`, which is invoked
   * per-render with a fresh `recordId`/options but never sees a resolved
   * `tableId`. Called with the resolved `tableId` on every render once
   * known. Falls back to the empty slice when it returns `undefined`.
   */
  getBufferedValue?: (tableId: string) => AdditionalTableDeltaSlice | undefined;
  /**
   * Called with this table's full delta slice whenever a buffered mutation
   * happens (buffered mode only) — synchronously in the event handler, computed
   * from the CURRENT effective buffered value plus the change. The parent
   * aggregates slices from every AdditionalTableTab instance on the page
   * (keyed by tableId) before sending the single save request. Ignored when
   * `buffered` is false.
   */
  onDeltasChange?: (tableId: string, delta: AdditionalTableDeltaSlice) => void;
  /**
   * Controlled add-row draft (Bug 2 — lost-on-tab-switch fix), used directly when
   * the caller already knows `tableId` up front. Mode-independent — applies whether
   * or not `buffered` is set. Mutually exclusive with `getAddRowDraft` (that one wins
   * if both are passed, same precedence rule as `bufferedValue`/`getBufferedValue`).
   */
  addRowDraft?: AdditionalTableAddRowDraft;
  /**
   * Getter variant of `addRowDraft` for callers that only know `tableSlug` up front
   * (e.g. `buildDocumentAdditionalRenderers`) — called with the resolved `tableId` on
   * every render once known. Falls back to the empty draft when it returns `undefined`.
   */
  getAddRowDraft?: (tableId: string) => AdditionalTableAddRowDraft | undefined;
  /**
   * Called with this table's full add-row draft whenever it changes. Supplying this
   * prop is what switches the component from an uncontrolled local draft (legacy
   * behavior — can only lose itself on an unwired remount) to a fully controlled one
   * owned by the parent, mirroring `onDeltasChange`.
   */
  onAddRowDraftChange?: (tableId: string, draft: AdditionalTableAddRowDraft) => void;
  /**
   * Wartości pól rekordu-RODZICA (kartoteki, pod którą żyje tabela) TAK, JAK SĄ NA EKRANIE —
   * także niezapisane zmiany: mapa slug pola → wartość (rodzic podaje mapę „nagłówek + pola
   * dodatkowe" z bieżącego stanu formularza, `buildAdditionalViewRecordValues`). Źródło wiązań
   * `parent_field` parametrów słowników w polach wierszy; do żądania opcji trafiają wyłącznie
   * sluggi użyte przez wiązania danego pola i tylko jako skalary. Stabilność referencji obiektu
   * NIE jest wymagana — klucz zapytania o opcje to zserializowane wybrane wartości, nie sam
   * obiekt.
   */
  parentValues?: Record<string, unknown>;
  /**
   * Klucz wyboru pola rodzica w wiązaniu `parent_field`: `'default'` dla pracowników,
   * kontrahentów i użytkowników, klasa dokumentu dla dokumentów. Bez niego żadne wartości
   * rodzica nie są wysyłane.
   */
  parentKey?: ParentFieldBindingKey;
  /**
   * Ustawienia umieszczenia tabeli na formatce (`element.settings` elementu layoutu) — źródło
   * układu kolumn: kolejność i widoczność (`tableColumns`, maska `hiddenColumnIds`), własna
   * etykieta, wyrównanie, szerokość i miejsca po przecinku. Pominięte albo bez zapisanego
   * układu = naturalna kolejność pól, wszystkie widoczne, rysunek jak przed układem kolumn.
   */
  columnSettings?: Record<string, unknown>;
}

/**
 * Client-generated id for a locally-inserted (unsaved) row in buffered mode.
 *
 * Uniqueness must hold across multiple adds AND across remounts (the buffer
 * survives remounts because it's now a controlled prop — see file header) —
 * and must never collide with a tempId already present in `existingInserted`
 * (rows carried over from a previous mount of this same component). A local
 * counter alone is unsafe: it resets to 0 on every remount, so the very first
 * tempId minted after a remount could collide with one already sitting in
 * the parent's buffer. Instead: seed from `Date.now()` (monotonic across
 * remounts within the same tab session) plus an in-page-lifetime counter for
 * same-millisecond adds, then loop to guarantee no collision against the
 * slice actually passed in.
 */
let tempIdCounter = 0;
function makeTempId(existingInserted: readonly AdditionalTableInsertedDelta[]): string {
  const existingIds = new Set(existingInserted.map((ins) => ins.tempId));
  let candidate: string;
  do {
    tempIdCounter += 1;
    candidate = `temp-${Date.now()}-${tempIdCounter}`;
  } while (existingIds.has(candidate));
  return candidate;
}

export function AdditionalTableTab({
  tableSlug,
  parentId,
  allowAddRow,
  editable = true,
  buffered = false,
  bufferedValue: bufferedValueProp,
  getBufferedValue,
  onDeltasChange,
  addRowDraft: addRowDraftProp,
  getAddRowDraft,
  onAddRowDraftChange,
  parentValues,
  parentKey,
  columnSettings,
}: AdditionalTableTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  // 1. Resolve tableId from slug via list endpoint
  const tablesQuery = useQuery({
    queryKey: additionalTablesKey(),
    queryFn: async () => {
      const res = await api.get<{ tables: AdditionalTableMeta[] }>('/settings/additional-tables');
      return res.data.tables;
    },
    staleTime: 60_000,
  });

  const tableMeta = tablesQuery.data?.find((tbl) => tbl.slug === tableSlug);
  const tableId = tableMeta?.id;
  // Read-only mode hides the bottom add-row entirely (mirror of the read-only
  // column filtering pattern used elsewhere for tables, e.g. DocumentVatRegisterEditor).
  const canAdd = (allowAddRow ?? tableMeta?.allowAddRow ?? false) && editable;

  // Effective controlled buffer value (Krok 7 bugfix) — re-derived on EVERY
  // render (not cached in local state), so a remount just re-reads whatever
  // the parent currently holds. `getBufferedValue` wins when both are
  // supplied (see prop doc); falls back to the empty slice when neither
  // resolves anything, matching pre-fix default behavior.
  const bufferedValue: AdditionalTableDeltaSlice =
    (tableId ? getBufferedValue?.(tableId) : undefined) ??
    bufferedValueProp ??
    EMPTY_ADDITIONAL_TABLE_DELTA_SLICE;
  // Tryb create (buffered, rekord jeszcze nie zapisany) — brak parentId.
  // Nie ma czego pobrać z serwera; zapytanie o istniejące wiersze jest zbędne
  // i błędne (parentId='' trafiłoby do zapytania).
  const hasParent = Boolean(parentId);

  // ─── Add-row draft (Bug 2: lost-on-tab-switch fix) ─────────────────────────
  // Same controlled-prop pattern as `bufferedValue` above, but for the
  // in-progress (not-yet-confirmed) bottom add-row — mode-independent (applies
  // whether or not `buffered` is set, since the loss happens in the typing
  // phase BEFORE confirm, not after). Hybrid: controlled when the parent
  // supplies `onAddRowDraftChange` (mirrors `bufferedValue`'s contract
  // exactly); falls back to a local `useState` otherwise, so unwired
  // callers/tests keep today's behavior — the fallback can only lose the
  // draft on an unwired remount, it never reports upward, so unlike the
  // pre-Krok-7 buffer it can't clobber parent state.
  const addRowDraftControlled = onAddRowDraftChange != null;
  const [localAddRowDraft, setLocalAddRowDraft] = useState<AdditionalTableAddRowDraft>(
    EMPTY_ADDITIONAL_TABLE_ADD_ROW_DRAFT,
  );
  const addRowDraft: AdditionalTableAddRowDraft = addRowDraftControlled
    ? ((tableId ? getAddRowDraft?.(tableId) : undefined) ??
      addRowDraftProp ??
      EMPTY_ADDITIONAL_TABLE_ADD_ROW_DRAFT)
    : localAddRowDraft;
  const setAddRowDraftValue = useCallback(
    (next: AdditionalTableAddRowDraft) => {
      if (addRowDraftControlled) {
        if (!tableId) return;
        onAddRowDraftChange?.(tableId, next);
        return;
      }
      setLocalAddRowDraft(next);
    },
    [addRowDraftControlled, tableId, onAddRowDraftChange],
  );
  // Najświeższy szkic dla zmian kończonych asynchronicznie (wysłanie pliku w wierszu dodawania):
  // wynik wysyłki trafia do BIEŻĄCEGO szkicu, a nie do kopii z chwili wyboru pliku — inaczej
  // nadpisałby wartości wpisane w trakcie wysyłania.
  const addRowDraftRef = useRef(addRowDraft);
  addRowDraftRef.current = addRowDraft;
  const setAddRowDraftField = useCallback(
    (slug: string, value: unknown) => {
      const current = addRowDraftRef.current;
      setAddRowDraftValue({ ...current, values: { ...current.values, [slug]: value } });
    },
    [setAddRowDraftValue],
  );
  const clearAddRowDraft = useCallback(
    () => setAddRowDraftValue(EMPTY_ADDITIONAL_TABLE_ADD_ROW_DRAFT),
    [setAddRowDraftValue],
  );

  // 2. Fetch fields
  const fieldsQuery = useQuery({
    queryKey: additionalFieldsKey(tableId ?? ''),
    // Ta sama funkcja pobiera definicje pól przy zapisie strony (`useAdditionalTableDeltas`),
    // gdy cache zdążył wygasnąć — jedno źródło kształtu odpowiedzi.
    queryFn: () => fetchAdditionalTableFields(tableId!),
    enabled: Boolean(tableId),
  });

  // 3. Fetch records (skip entirely gdy brak parentId — patrz `hasParent` wyżej)
  // Kształt odpowiedzi: `{ records, extRefLabels? }` — mapa-siostra etykiet odwołań
  // (ten sam kontrakt co listy kartotek), dokładana przez backend WYŁĄCZNIE gdy tabela
  // ma choć jedno skonfigurowane pole `reference` (patrz `resolveAdditionalRefLabels`).
  const recordsQuery = useQuery({
    queryKey: additionalRecordsKey(tableId ?? '', parentId ?? ''),
    queryFn: async () => {
      const res = await api.get<{
        records: AdditionalRecord[];
        extRefLabels?: ExtRefLabelMap;
        /** Nazwy plików kolumn „plik” (ADR-0114), klucz = id pliku z wartości. */
        fileMeta?: Record<string, FieldFileMeta>;
      }>(`/settings/additional-tables/${tableId}/records`, {
        params: { parentId: parentId ?? '' },
      });
      return res.data;
    },
    enabled: Boolean(tableId) && hasParent,
  });

  // Zapis pojedynczego wiersza (tryb bez bufora — POST/PATCH od razu): wartości pól rodzica
  // z ekranu dla wiązań `parent_field`, tylko sluggi użyte przez pola tej tabeli i tylko
  // skalary. Pusty obiekt = nic do wysłania (pole w ogóle nie trafia do body). Serwer sam
  // wyprowadza klucz wiązania z rodzica tabeli — `parentKey` NIE jedzie w body.
  const singleRowParentValuesBody = (): { parentValues?: AdditionalParentValues } => {
    if (parentKey === undefined) return {};
    const slugs = collectParentFieldSlugsForFields(fieldsQuery.data ?? [], parentKey);
    return slugs.length > 0 ? { parentValues: pickParentValues(parentValues ?? {}, slugs) } : {};
  };

  // 4. Add row mutation
  const addMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const res = await api.post<{ record: AdditionalRecord }>(
        `/settings/additional-tables/${tableId}/records`,
        { parentId: parentId ?? '', values, ...singleRowParentValuesBody() },
      );
      return res.data.record;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: additionalRecordsKey(tableId!, parentId ?? ''),
      });
      toast.show({ variant: 'success', title: t('additionalTables.addSuccess') });
      // m4/Bug 2: clear the (controlled or local) draft only after a successful POST.
      clearAddRowDraft();
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: mapDictionarySaveError(err, t, 'tableRow') ?? t('additionalTables.addError'),
      });
      // m4: do NOT reset on failure — typed input is preserved so the user can retry
    },
  });

  // 5. Patch single cell mutation
  const patchMutation = useMutation({
    mutationFn: async ({
      recordId,
      fieldSlug,
      newValue,
    }: {
      recordId: string;
      fieldSlug: string;
      newValue: unknown;
    }) => {
      const res = await api.patch<{ record: AdditionalRecord }>(
        `/settings/additional-tables/${tableId}/records/${recordId}`,
        { values: { [fieldSlug]: newValue }, ...singleRowParentValuesBody() },
      );
      return res.data.record;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: additionalRecordsKey(tableId!, parentId ?? ''),
      });
      toast.show({ variant: 'success', title: t('additionalTables.editSuccess') });
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: mapDictionarySaveError(err, t, 'tableRow') ?? t('additionalTables.editError'),
      });
    },
  });

  // 6. Delete row mutation
  const deleteMutation = useMutation({
    mutationFn: async (recordId: string) => {
      await api.delete(`/settings/additional-tables/${tableId}/records/${recordId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: additionalRecordsKey(tableId!, parentId ?? ''),
      });
      toast.show({ variant: 'success', title: t('additionalTables.deleteSuccess') });
      setPendingDeleteId(null);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('additionalTables.deleteError') });
    },
  });

  // ─── Local state ───────────────────────────────────────────────────────────

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  /**
   * Wiersz dodawania jest schowany, dopóki user nie kliknie „Dodaj wiersz” (wzorzec Pozycji
   * dokumentu). Po dodaniu zostaje otwarty pod kolejny wpis; zamyka go ✕ albo Esc. Szkic z
   * wpisanymi wartościami trzyma wiersz widoczny także po przełączeniu zakładek (lokalny stan
   * ginie przy odmontowaniu, szkic — nie).
   */
  const [addRowOpen, setAddRowOpen] = useState(false);
  // Koniec edycji (Zapisz/Anuluj) zamyka wiersz dodawania — kolejna edycja zaczyna od zamkniętego.
  const [wasEditable, setWasEditable] = useState(editable);
  if (wasEditable !== editable) {
    setWasEditable(editable);
    if (!editable) setAddRowOpen(false);
  }
  const showAddRow = canAdd && (addRowOpen || draftHasValues(addRowDraft));
  const closeAddRow = useCallback(() => {
    clearAddRowDraft();
    setAddRowOpen(false);
  }, [clearAddRowDraft]);

  /**
   * Etykiety ostatnio wybranych rekordów dla pól `reference`, kluczowane
   * `${recordId}:${fieldSlug}` — żyje na poziomie CAŁEJ tabeli (nie pojedynczego
   * wiersza), więc przetrwa to, że szkic add-row (`addRowDraft` niżej) wraca do
   * pustego stanu zaraz po udanym dodaniu — to właśnie ten reset sprawiał, że
   * świeżo dodany wiersz z referencją ZAWSZE pokazywał skrócone UUID zamiast
   * wybranej nazwy, aż do zapisu całej kartoteki (nowy wiersz nie ma jeszcze
   * wpisu w `extRefLabels` z backendu). NIE przetrwa przełączenia zakładek Tabs
   * (cała `AdditionalTableTab` jest wtedy odmontowywana razem z tym stanem) —
   * pozostałe, węższe ograniczenie, patrz JSDoc `AdditionalReferenceCell`.
   */
  const [pickedLabels, setPickedLabels] = useState<Record<string, string>>({});

  /**
   * Metadane plików wysłanych w tej sesji edycji (kolumny „plik”, ADR-0114) — serwer zna je
   * dopiero po zapisie rekordu (`fileMeta` w odczycie wierszy), a do tego czasu komórka ma
   * pokazać nazwę wybranego pliku, nie identyfikator.
   */
  const [uploadedFileMeta, setUploadedFileMeta] = useState<Record<string, FieldFileMeta>>({});
  const rememberFileMeta = useCallback((fileId: string, meta: FieldFileMeta) => {
    setUploadedFileMeta((prev) => (prev[fileId] ? prev : { ...prev, [fileId]: meta }));
  }, []);
  const setPickedLabel = useCallback(
    (recordId: string, fieldSlug: string, label: string | null) => {
      const key = `${recordId}:${fieldSlug}`;
      setPickedLabels((prev) => {
        if (label == null) {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        }
        if (prev[key] === label) return prev;
        return { ...prev, [key]: label };
      });
    },
    [],
  );

  // ─── Buffered-mode handlers (Krok 7 controlled-buffer bugfix) ──────────────
  // No local buffer state. Each mutation derives the NEW slice from the
  // CURRENT `bufferedValue` prop plus the change and reports it synchronously
  // via `onDeltasChange` — the parent owns the buffer, so a remount of this
  // component (e.g. switching Tabs away and back) just re-reads the parent's
  // still-intact value instead of resetting to empty and clobbering it.

  const handleBufferedAdd = useCallback(
    (values: Record<string, unknown>) => {
      if (!tableId) return null;
      const tempId = makeTempId(bufferedValue.inserted);
      onDeltasChange?.(tableId, {
        ...bufferedValue,
        inserted: [...bufferedValue.inserted, { tableId, tempId, values }],
      });
      // Parity with the HTTP path: clear the add-row draft after a successful "add".
      clearAddRowDraft();
      return tempId;
    },
    [tableId, bufferedValue, onDeltasChange, clearAddRowDraft],
  );

  const handleBufferedCommitCell = useCallback(
    (recordId: string, fieldSlug: string, newValue: unknown) => {
      if (!tableId) return;
      // If this row is itself a locally-inserted (unsaved) row, update the
      // inserted entry's values instead of creating an `updated` delta.
      const insertedIdx = bufferedValue.inserted.findIndex((ins) => ins.tempId === recordId);
      if (insertedIdx !== -1) {
        const nextInserted = bufferedValue.inserted.slice();
        const existing = nextInserted[insertedIdx]!;
        nextInserted[insertedIdx] = {
          ...existing,
          values: { ...existing.values, [fieldSlug]: newValue },
        };
        onDeltasChange?.(tableId, { ...bufferedValue, inserted: nextInserted });
        return;
      }
      const updatedIdx = bufferedValue.updated.findIndex((upd) => upd.recordId === recordId);
      const nextUpdated = bufferedValue.updated.slice();
      const existingValues = updatedIdx !== -1 ? nextUpdated[updatedIdx]!.values : {};
      const newEntry: AdditionalTableUpdatedDelta = {
        tableId,
        recordId,
        values: { ...existingValues, [fieldSlug]: newValue },
      };
      if (updatedIdx !== -1) {
        nextUpdated[updatedIdx] = newEntry;
      } else {
        nextUpdated.push(newEntry);
      }
      onDeltasChange?.(tableId, { ...bufferedValue, updated: nextUpdated });
    },
    [tableId, bufferedValue, onDeltasChange],
  );

  const handleBufferedDelete = useCallback(
    (recordId: string) => {
      if (!tableId) return;
      // Deleting a locally-inserted (unsaved) row just drops it — no removed entry needed.
      if (bufferedValue.inserted.some((ins) => ins.tempId === recordId)) {
        onDeltasChange?.(tableId, {
          ...bufferedValue,
          inserted: bufferedValue.inserted.filter((ins) => ins.tempId !== recordId),
        });
      } else {
        // Drop any pending edit for this row (it's about to be removed) and mark removed.
        const nextUpdated = bufferedValue.updated.filter((upd) => upd.recordId !== recordId);
        const nextRemoved = bufferedValue.removed.some((rem) => rem.recordId === recordId)
          ? bufferedValue.removed
          : [...bufferedValue.removed, { tableId, recordId }];
        onDeltasChange?.(tableId, { ...bufferedValue, updated: nextUpdated, removed: nextRemoved });
      }
      setPendingDeleteId(null);
    },
    [tableId, bufferedValue, onDeltasChange],
  );

  /** Przywraca wiersz oznaczony do usunięcia (zdejmuje go z `removed`). */
  const handleBufferedRestore = useCallback(
    (recordId: string) => {
      if (!tableId) return;
      onDeltasChange?.(tableId, {
        ...bufferedValue,
        removed: bufferedValue.removed.filter((rem) => rem.recordId !== recordId),
      });
    },
    [tableId, bufferedValue, onDeltasChange],
  );

  /** Cofa niezapisane zmiany zapisanego wiersza — wraca do wartości z serwera. */
  const handleBufferedRevert = useCallback(
    (recordId: string) => {
      if (!tableId) return;
      onDeltasChange?.(tableId, {
        ...bufferedValue,
        updated: bufferedValue.updated.filter((upd) => upd.recordId !== recordId),
      });
    },
    [tableId, bufferedValue, onDeltasChange],
  );

  /** Usunięcie wiersza: w trybie buforowanym od razu (da się przywrócić do zapisu), bez bufora —
   *  przez okno potwierdzenia, bo DELETE idzie na serwer natychmiast. */
  const requestDelete = (recordId: string) => {
    if (!editable) return;
    if (buffered) {
      handleBufferedDelete(recordId);
      return;
    }
    setPendingDeleteId(recordId);
  };

  // ─── Cell commit handler ───────────────────────────────────────────────────

  const handleCommitCell = useCallback(
    (recordId: string, fieldSlug: string, newValue: unknown) => {
      // Defensive guard — the UI path is already fully gated (read-only cells
      // never render an editable control), this makes "no edit" true by
      // construction rather than only via the absent DOM element.
      if (!editable) return;
      if (buffered) {
        handleBufferedCommitCell(recordId, fieldSlug, newValue);
        return;
      }
      patchMutation.mutate({ recordId, fieldSlug, newValue });
    },
    [editable, buffered, handleBufferedCommitCell, patchMutation],
  );

  // ─── Loading / error states ────────────────────────────────────────────────

  if (tablesQuery.isPending) {
    return (
      <div className="p-4 text-sm text-text-muted" data-testid="additional-loading">
        {t('common.loading')}
      </div>
    );
  }

  if (!tableMeta || !tableId) {
    return (
      <div className="p-4 text-sm text-text-tertiary" data-testid="additional-no-table">
        {tableSlug
          ? t('additionalTables.tableNotFound', { slug: tableSlug })
          : t('additionalTables.noTableConfigured')}
      </div>
    );
  }

  // recordsQuery jest `enabled: false` gdy `!hasParent` (tryb create) — w tym
  // wypadku `isPending` zostałoby `true` na zawsze (disabled query nigdy nie
  // rozstrzyga), więc pomijamy je w warunku i traktujemy jako brak wierszy.
  if (fieldsQuery.isPending || (hasParent && recordsQuery.isPending)) {
    return (
      <div className="p-4 text-sm text-text-muted" data-testid="additional-loading">
        {t('common.loading')}
      </div>
    );
  }

  if (fieldsQuery.isError || recordsQuery.isError) {
    return (
      <div className="p-4 text-sm text-red-600" data-testid="additional-error">
        {t('additionalTables.loadError')}
      </div>
    );
  }

  const fields = (fieldsQuery.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  // Układ kolumn z `columnSettings` — jedna lista widocznych kolumn dla nagłówka, wierszy i
  // wiersza dodawania. Zwykła funkcja (nie hook), bo stoi za wczesnymi `return`ami.
  const columns = buildColumnViews(fields, columnSettings);
  // Tabela ma pola, ale układ ukrył je wszystkie (możliwe tylko bez pól wymaganych).
  const allColumnsHidden = columns.length === 0 && fields.length > 0;
  // Jawna szerokość choć jednej kolumny → `table-layout: fixed` + `<colgroup>`; bez niej tabela
  // zostaje w układzie automatycznym, jak dotąd.
  const fixedLayout = hasExplicitWidths(columns);
  const trailingWidths = editable ? [ACTIONS_COLUMN_WIDTH_PX] : [];
  // Minimum szerokości tabeli w układzie stałym — w wąskim kontenerze tabela rośnie (przewijanie),
  // zamiast zapadać kolumny bez szerokości do 0 px.
  const tableMinWidth = fixedTableMinWidth(columns, trailingWidths);
  const serverRecords = recordsQuery.data?.records ?? [];
  const extRefLabels: ExtRefLabelMap = recordsQuery.data?.extRefLabels ?? {};
  const fileMeta: Record<string, FieldFileMeta> = {
    ...(recordsQuery.data?.fileMeta ?? {}),
    ...uploadedFileMeta,
  };
  const fileTargetFor = (fieldSlug: string): FieldFileTarget => ({
    tableId,
    fieldSlug,
    parentId: parentId ?? null,
  });

  // In buffered mode, overlay the CONTROLLED bufferedValue prop on top of the
  // server snapshot: keep removed rows (shown struck through, restorable until
  // save), merge pending edits into updated rows, append locally-inserted rows
  // (rendered with their tempId as the row id). Since bufferedValue comes from
  // the parent, this overlay is correct immediately after a remount — nothing
  // to lose (Krok 7 bugfix).
  const rows: Array<{ record: AdditionalRecord; status: RowStatus }> = buffered
    ? [
        ...serverRecords.map((r) => {
          if (bufferedValue.removed.some((rem) => rem.recordId === r.id)) {
            return { record: r, status: 'removed' as const };
          }
          const pendingEdit = bufferedValue.updated.find((upd) => upd.recordId === r.id);
          return pendingEdit
            ? { record: { ...r, ...pendingEdit.values }, status: 'changed' as const }
            : { record: r, status: 'saved' as const };
        }),
        ...bufferedValue.inserted.map((ins) => ({
          record: {
            id: ins.tempId,
            parent_id: parentId ?? '',
            position: ins.position ?? 0,
            created_at: '',
            updated_at: '',
            ...ins.values,
          } as AdditionalRecord,
          status: 'new' as const,
        })),
      ]
    : serverRecords.map((record) => ({ record, status: 'saved' as const }));
  // Liczba niezapisanych zmian tej tabeli (dodane + zmienione + do usunięcia) — tylko w edycji.
  const pendingCount =
    buffered && editable
      ? bufferedValue.inserted.length + bufferedValue.updated.length + bufferedValue.removed.length
      : 0;
  const columnCount = columns.length + (editable ? 1 : 0);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3" data-testid="additional-table-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-text-primary">{tableMeta.displayName}</h4>
        {pendingCount > 0 && (
          <Badge tone="warning" size="md" data-testid="additional-pending-count">
            {t('additionalTables.pendingChanges', { count: pendingCount })}
          </Badge>
        )}
      </div>

      {/* Table (always rendered when fields are loaded) */}
      {allColumnsHidden ? (
        // Układ formularza nie ma żadnej widocznej kolumny — tabela bez kolumn i wiersz dodawania
        // bez pól nie mają sensu, więc zamiast nich krótki komunikat. Edytor układu nie pozwala
        // ukryć ostatniej widocznej kolumny (decyzja z 2026-10-21, docs/OPEN_QUESTIONS.md), więc
        // to zapas na przypadki spoza edytora — np. w ustawieniach usunięto wszystkie widoczne pola.
        <p className="text-sm text-text-tertiary" data-testid="additional-all-columns-hidden">
          {t('additionalTables.allColumnsHidden')}
        </p>
      ) : rows.length === 0 && !canAdd ? (
        <p className="text-sm text-text-tertiary" data-testid="additional-empty">
          {t('additionalTables.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-primary">
          <table
            className={fixedLayout ? 'w-full table-fixed text-sm' : 'w-full text-sm'}
            style={tableMinWidth !== undefined ? { minWidth: `${tableMinWidth}px` } : undefined}
            data-testid="additional-records-table"
          >
            <TableColgroup columns={columns} trailingWidths={trailingWidths} />
            <thead>
              <tr className="border-b border-border-primary bg-bg-secondary">
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={`px-3 py-2 ${col.alignClass ?? 'text-left'} text-xs font-semibold text-text-secondary`}
                  >
                    {col.label}
                  </th>
                ))}
                {editable && (
                  <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ record, status }) => (
                <tr
                  key={record.id}
                  className={cn(
                    'border-b border-border-primary last:border-0',
                    ROW_STATUS_CLASS[status],
                  )}
                  data-testid={`additional-row-${record.id}`}
                  data-row-status={status}
                >
                  {columns.map((col) => (
                    <InlineCellTd
                      key={col.id}
                      field={col.field}
                      record={record}
                      // Wiersz do usunięcia jest tylko podglądem — przed edycją trzeba go przywrócić.
                      editable={editable && status !== 'removed'}
                      onCommitCell={handleCommitCell}
                      resolvedReferenceLabel={referenceLabelForCell(
                        col.field,
                        record,
                        extRefLabels,
                        t,
                      )}
                      optimisticReferenceLabel={pickedLabels[`${record.id}:${col.id}`]}
                      onReferenceLabelPicked={(label) => setPickedLabel(record.id, col.id, label)}
                      parentValues={parentValues}
                      parentKey={parentKey}
                      alignClass={col.alignClass}
                      decimals={col.decimals}
                      {...(col.field.fieldType === 'file'
                        ? {
                            fileMeta: fileMetaForCell(record[col.id], fileMeta),
                            fileTarget: fileTargetFor(col.id),
                            onFileMeta: rememberFileMeta,
                          }
                        : {})}
                    />
                  ))}
                  {editable && (
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      {status === 'removed' ? (
                        <IconButton
                          size="sm"
                          className="text-accent-600 hover:text-accent-700"
                          onClick={() => handleBufferedRestore(record.id)}
                          aria-label={t('additionalTables.restoreRow')}
                          title={t('additionalTables.restoreRow')}
                          data-testid={`additional-restore-btn-${record.id}`}
                        >
                          <Undo2 />
                        </IconButton>
                      ) : (
                        <>
                          {status === 'changed' && (
                            <IconButton
                              size="sm"
                              onClick={() => handleBufferedRevert(record.id)}
                              aria-label={t('additionalTables.revertRow')}
                              title={t('additionalTables.revertRow')}
                              data-testid={`additional-revert-btn-${record.id}`}
                            >
                              <Undo2 />
                            </IconButton>
                          )}
                          <IconButton
                            size="sm"
                            variant="danger"
                            onClick={() => requestDelete(record.id)}
                            aria-label={t('additionalTables.deleteRow')}
                            title={t('additionalTables.deleteRow')}
                            data-testid={`additional-delete-btn-${record.id}`}
                          >
                            <Trash2 />
                          </IconButton>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}

              {/* Pusta tabela w edycji — podpowiedź jak w Pozycjach dokumentu. */}
              {rows.length === 0 && !showAddRow && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-3 py-6 text-center text-sm text-text-tertiary"
                    data-testid="additional-empty-hint"
                  >
                    {t('additionalTables.emptyHint')}
                  </td>
                </tr>
              )}

              {/* Wiersz dodawania — widoczny po kliknięciu „Dodaj wiersz” (albo gdy szkic ma wartości). */}
              {showAddRow && (
                <AddRow
                  columns={columns}
                  draft={addRowDraft}
                  onDraftChange={setAddRowDraftValue}
                  onCancel={closeAddRow}
                  autoFocusFirst={addRowOpen}
                  fileMeta={fileMeta}
                  fileTargetFor={fileTargetFor}
                  onFileMeta={rememberFileMeta}
                  onDraftFieldChange={setAddRowDraftField}
                  onAdd={(values, labels) => {
                    if (!buffered) {
                      addMutation.mutate(values);
                      return;
                    }
                    const tempId = handleBufferedAdd(values);
                    if (tempId && labels) {
                      for (const [slug, label] of Object.entries(labels)) {
                        setPickedLabel(tempId, slug, label);
                      }
                    }
                  }}
                  adding={!buffered && addMutation.isPending}
                  parentValues={parentValues}
                  parentKey={parentKey}
                />
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stopka: „Dodaj wiersz” (wzorzec Pozycji dokumentu) + podpowiedź, kiedy zmiany się zapiszą. */}
      {!allColumnsHidden && (canAdd || pendingCount > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {canAdd && !showAddRow ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAddRowOpen(true)}
              data-testid="additional-add-row-btn"
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('additionalTables.addRow')}
            </Button>
          ) : (
            <span />
          )}
          {pendingCount > 0 && (
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary"
              data-testid="additional-pending-hint"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-0.5 bg-accent-500" aria-hidden="true" />
                {t('additionalTables.legendNew')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-0.5 bg-amber-500" aria-hidden="true" />
                {t('additionalTables.legendChanged')}
              </span>
              <span className="line-through">{t('additionalTables.legendRemoved')}</span>
              <span>{t('additionalTables.pendingHint')}</span>
            </div>
          )}
        </div>
      )}

      {/* Delete confirm (tylko tryb bez bufora — tam DELETE idzie od razu na serwer) */}
      <ConfirmDialog
        open={pendingDeleteId !== null}
        title={t('additionalTables.confirmDeleteTitle')}
        message={t('additionalTables.confirmDeleteMessage')}
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          // Defensive guard (see handleCommitCell) — unreachable via the UI
          // today since the delete button/column is itself hidden when
          // !editable, but keeps "no edit" true by construction.
          if (!editable || !pendingDeleteId) return;
          if (buffered) {
            handleBufferedDelete(pendingDeleteId);
          } else {
            deleteMutation.mutate(pendingDeleteId);
          }
        }}
        onClose={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
